import { computed, unref } from 'vue'
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import { useSessionValues } from '@/composables/useSessionValues'

import * as Sentry from '@sentry/vue'

import OrganisationManagementService from '@/services/OrganisationManagementService'
import apis from '@/services'

export function useGetUserCurrency({ isGuest }) {
  const { token } = useSessionValues()
  const { isLoading, isFetching, data, fetchStatus } = useQuery({
    queryKey: ['organisationCurrency', token, isGuest],
    enabled: computed(() => {
      // Check if token ref even exists
      if (!token || token.value === undefined) {
        return false
      }

      const isGuestUser = unref(isGuest)

      // For guests, always enabled (if we have the ref)
      if (isGuestUser) return true

      // For users, wait for actual token value
      return token.value !== null
    }),
    queryFn: async () => {
      if (!isGuest.value && token.value !== null) {
        const response = await apis.premium.getUserCurrency()
        return response.data
      } else {
        const response = await apis.premium.getGuestCurrency()
        return response.data
      }
    },
    onError: error => {
      Sentry.withScope(scope => {
        scope.setTag('feature', 'currency-selector-getOrganisationCurrency')
        scope.setExtra('Response error', error)
        Sentry.captureException(error)
      })
    },
  })

  const isOrganisationsCurrencyLoading = computed(() => isLoading.value && fetchStatus.value !== 'idle')
  const currencyData = computed(() => data.value)
  const currency = computed(() => data.value?.currency)
  const language = computed(() => data.value?.language?.languageCode)

  return {
    isOrganisationsCurrencyLoading,
    isOrganisationCurrencyFetching: isFetching,
    userCurrency: currency,
    currencyData,
    userLanguage: language,
  }
}

export function useUpdateCurrency() {
  const queryClient = useQueryClient()
  const {
    isLoading: updatingUserCurrencyLoading,
    isSuccess: updatingUserCurrencySuccess,
    isError: updatingUserCurrencyError,
    mutate: updateCurrency,
  } = useMutation({
    mutationFn: ({ userId, currency }) =>
      OrganisationManagementService.updateUserProfile({ userId, body: { currency } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organisationCurrency'] })
    },
    onError: error => {
      Sentry.withScope(scope => {
        scope.setTag('feature', 'currency-selector-updateCurrency')
        scope.setExtra('Response error', error)
        Sentry.captureException(error)
      })
    },
  })

  return {
    updatingUserCurrencyLoading,
    updatingUserCurrencySuccess,
    updatingUserCurrencyError,
    updateCurrency,
  }
}
